import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { AuthContext } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import { runAdmob, useAdsListener } from '../../../services/admob';
import { useRunTimeEnvironment } from '../../../utils/hooks';
import { LIGHTNING_DAILY_MAX, LIGHTNING_SHOP_NO, SNACK_STORAGE_MAX } from './snackShopModel';

export function useSnackShopData() {
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const eggKey = accessToken && [`${API_SERVER_DOMAIN}/game/egg/userGameInfo`, accessToken];
  const shopKey = accessToken && [`${API_SERVER_DOMAIN}/game/egg/snackShopInfo`, accessToken];
  const { data: eggData, mutate: mutateEgg, isLoading: isEggLoading } = useSWR(eggKey, fetchWithToken);
  const { data: shopData, mutate: mutateShop, isLoading: isShopLoading } = useSWR(shopKey, fetchWithToken);

  const snackCount = eggData?.userEggGameInfo?.snackCount ?? 0;
  const lightningUsed = eggData?.userEggGameInfo?.dailyStationChargeCount ?? 0;
  const slots = useMemo(() => {
    const list = Array.isArray(shopData?.snackShopInfo) ? shopData.snackShopInfo : [];
    return list
      .filter((item) => Number(item?.snackShopNo) !== LIGHTNING_SHOP_NO)
      .sort((a, b) => (Number(a?.startAt) || 0) - (Number(b?.startAt) || 0));
  }, [shopData]);

  const refresh = useCallback(async () => {
    await Promise.all([mutateEgg(), mutateShop()]);
  }, [mutateEgg, mutateShop]);

  return {
    ready: !accessToken || (!isEggLoading && !isShopLoading),
    snackCount,
    lightningUsed,
    lightningRemaining: Math.max(0, LIGHTNING_DAILY_MAX - lightningUsed),
    slots,
    refresh,
  };
}

export function useSnackCharge({ snackCount, onCharged }) {
  const { API_SERVER_DOMAIN, useAccessToken } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const runtime = useRunTimeEnvironment();
  const { showToast, showErrorToast } = useToast();
  const pendingRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onChargedRef = useRef(onCharged);
  onChargedRef.current = onCharged;

  const handleAdError = useCallback(() => {
    pendingRef.current = null;
    setBusy(false);
    showErrorToast('광고를 완료하지 못해 간식을 받지 못했어요.');
  }, [showErrorToast]);

  const adsParams = useMemo(() => ({ task: 'snack-shop', onError: handleAdError }), [handleAdError]);

  const finishCharge = useCallback(async () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending || !accessToken) {
      setBusy(false);
      return;
    }

    try {
      const response = await fetch(`${API_SERVER_DOMAIN}/game/egg/receiveSnack`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ snack_shop_no: pending.shopNo }),
      });
      const json = await response.json();
      if (json?.result !== 'ok' || Number(json?.errorCode) !== 0) {
        showErrorToast('지금은 간식을 받을 수 없어요.');
        return;
      }
      showToast(`간식 ${Number(pending.rewardCount).toLocaleString()}개를 충전했어요.`);
      await onChargedRef.current?.();
    } catch (error) {
      showErrorToast('간식 충전에 실패했어요.');
    } finally {
      setBusy(false);
    }
  }, [API_SERVER_DOMAIN, accessToken, showErrorToast, showToast]);

  const finishRef = useRef(finishCharge);
  finishRef.current = finishCharge;
  const completeAd = useCallback(() => {
    if (!pendingRef.current) return;
    finishRef.current();
  }, []);

  useAdsListener('', '', adsParams, completeAd);

  useEffect(() => {
    if (!busy) return undefined;
    const timeoutId = window.setTimeout(() => {
      pendingRef.current = null;
      setBusy(false);
    }, 90000);
    return () => window.clearTimeout(timeoutId);
  }, [busy]);

  const requestCharge = useCallback(
    (shopNo, rewardCount) => {
      if (busy) return;
      if (snackCount >= SNACK_STORAGE_MAX || snackCount + rewardCount > SNACK_STORAGE_MAX) {
        showToast('간식은 최대 999개까지 보관할 수 있어요.');
        return;
      }
      if (runtime !== 'APP') {
        alert('광고 시청은 앱에서만 가능해요.');
        return;
      }
      pendingRef.current = { shopNo, rewardCount };
      setBusy(true);
      runAdmob('');
    },
    [busy, runtime, showToast, snackCount],
  );

  return { busy, requestCharge };
}
